import {
    type AfterViewInit,
    Component,
    HostListener,
    type OnDestroy,
    type OnInit,
    inject,
    DOCUMENT,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { setTheme } from 'ngx-bootstrap/utils';
import { TranslateService } from '@ngx-translate/core';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { esLocale, frLocale, itLocale, ptBrLocale, ruLocale, trLocale, ukLocale } from 'ngx-bootstrap/locale';
import { Actions, ofType } from '@ngrx/effects';
import { combineLatest, type Observable, shareReplay, Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import type { SiteListEntity } from '@common/types/site.type';

import { AppFacade } from '@web/store/app/app.facade';
import { SitesCollectionFacade } from '@web/store/collections/sites/sites.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';

import AppActions from '@web/store/app/app.actions';
import { UserFacade } from '@web/store/user/user.facade';
import { FmFeatureApiService } from '@sendpulse/file-manager';
import { WebTabGuardService } from '@web/core/services/web-tab-guard.service';
import { OnboardingWizardAbTestService } from '@web/core/services/onboarding-wizard-ab-test.service';
import { PageTitleService } from '@web/core/services/page-title.service';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { isUuid } from '@common/helpers';
import type { TourOptions } from 'intro.js/src/packages/tour/option';
import type { TourStep } from 'intro.js/src/packages/tour/steps';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';

const locales = [esLocale, frLocale, ptBrLocale, ruLocale, trLocale, ukLocale, itLocale];
locales.forEach((locale) => defineLocale(locale.abbr, locale));

@Component({
    selector: 'web-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly translate = inject(TranslateService);
    private readonly bsLocaleService = inject(BsLocaleService);
    private readonly store$ = inject(Store);
    private readonly appFacade = inject(AppFacade);
    private readonly actions$ = inject(Actions);
    private readonly contentFacade = inject(ContentFacade);
    private readonly sitesCollectionFacade = inject(SitesCollectionFacade);
    private readonly userFacade = inject(UserFacade);
    private readonly fmFeatureApiService = inject(FmFeatureApiService);
    private readonly webTabGuardService = inject(WebTabGuardService);
    private readonly onboardingWizardAbTestService = inject(OnboardingWizardAbTestService);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly pageTitleService = inject(PageTitleService);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly pagesFacade = inject(PagesFacade);

    public readonly isReady$: Observable<boolean> = this.appFacade.isReady$;
    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly isDirty$: Observable<boolean> = this.contentFacade.isDirty$;

    public readonly sites$: Observable<SiteListEntity[]> = this.appFacade.isAdminCollectionWrapper$(
        this.sitesCollectionFacade.sites$
    );

    private readonly ONBOARDING_KEY = 'sp-web-pages-onboard';
    private readonly AUTO_SAVE_ONBOARDING_KEY = 'sp-web-pages-auto-save-onboard';
    private readonly destroy$ = new Subject<void>();

    private readonly introOptions: Partial<TourOptions> = {
        nextLabel: this.translate.instant('next'),
        prevLabel: this.translate.instant('back'),
        doneLabel: this.translate.instant('done'),
        skipLabel: '×',
        tooltipPosition: 'bottom',
        tooltipClass: 'sp-intro',
        exitOnEsc: true,
        exitOnOverlayClick: false,
        showStepNumbers: false,
        scrollToElement: false,
    };

    @HostListener('window:beforeunload', ['$event'])
    async showExitModal($event): Promise<void> {
        let isDirty = null;
        const subscriber = await this.isDirty$.subscribe((res) => (isDirty = res));
        if (subscriber && isDirty) {
            $event.returnValue = true;
        }
    }

    public ngOnInit(): void {
        // Must run before any in-app navigation drops the entry query params.
        this.onboardingWizardAbTestService.captureEntryParams();

        setTheme('bs3');
        this.bsLocaleService.use(this.getLocaleForBootstrap(this.translate.currentLang));
        this.isAliveSession();

        const html = this.document.querySelector('html');
        html.setAttribute('lang', this.translate.currentLang);
        this.fmFeatureApiService.cdnInfo$
            .pipe(take(1), filter(Boolean), shareReplay(1))
            .subscribe((cdnInfo) => this.userFacade.setUserCdnInfo(cdnInfo));
        this.webTabGuardService.enterConstructor();
        this.appFacade.getDataLayer();
        this.onboardingWizardAbTestService.initTracking();
        // Toggled on <body> (not the host) so the --navbar-height override also reaches body-level
        // CDK overlays such as the file manager drawer.
        this.onboardingWizardAbTestService.showOnboardingSteps$
            .pipe(takeUntil(this.destroy$))
            .subscribe((show) => this.document.body.classList.toggle('has-onboarding-steps', show));
        // Resize handles track their drag on `document`, so the pointer leaves the handle on the
        // first move — the cursor has to be pinned app-wide for as long as a resize is running.
        // Driven off the store rather than from the handles: they already report the gesture there.
        this.layoutFacade.isColumnWidthChanging$
            .pipe(takeUntil(this.destroy$))
            .subscribe((resizing) => this.document.body.classList.toggle('column-resizing', resizing));
        this.subscribeToPageTitleUpdates();
    }

    public ngAfterViewInit(): void {
        this.actions$
            .pipe(ofType(AppActions.initAppSuccess), withLatestFrom(this.isAdmin$), take(1))
            .subscribe(([, isAdmin]) => {
                const onboarded = JSON.parse(localStorage.getItem(this.ONBOARDING_KEY));
                const desktopMediaQuery = window.matchMedia('(min-width: 992px)');

                if (!onboarded && !isAdmin && desktopMediaQuery.matches) {
                    setTimeout(() => {
                        this.startOnboarding();
                    }, 2000);
                }
            });
        this.routerFacade.routerParams$
            .pipe(
                take(1),
                filter(({ siteId }) => !!siteId && !isUuid(siteId))
            )
            .subscribe(({ siteId }) => {
                this.shopCartIconFacade.loadCartIcon(siteId);
            });
    }

    public getLocaleForBootstrap(locale: string): string {
        switch (locale) {
            case 'ua':
                return 'uk';
            case 'es-mx':
                return 'es';
            default:
                return locale;
        }
    }

    private isAliveSession() {
        setInterval(() => this.appFacade.updateSession(), 1000 * 60 * 10);
    }

    public async startOnboarding() {
        const introLib = await import('intro.js');
        const introJS = introLib.default();

        const steps: Partial<TourStep>[] = [];

        if (document.querySelector('#introThemeSettings')) {
            steps.push({
                element: document.querySelector('#introThemeSettings'),
                title: this.translate.instant('onboarding_title_step_1'),
                intro: this.translate.instant('onboarding_description_step_1'),
                position: 'left',
            });
        }

        steps.push({
            intro:
                '<img class="sp-intro-animation" src="./assets/img/websites/intro/sp-intro-step-2-anim-new.gif">' +
                this.translate.instant('onboarding_description_step_2'),
            title: this.translate.instant('onboarding_title_step_2'),
            position: 'left',
            tooltipClass: 'sp-intro sp-intro-wide',
        });

        steps.push({
            intro:
                '<img class="sp-intro-animation" src="./assets/img/websites/intro/sp-intro-step-3-anim-new.gif">' +
                this.translate.instant('onboarding_description_step_3'),
            title: this.translate.instant('onboarding_title_step_3'),
            position: 'left',
            tooltipClass: 'sp-intro sp-intro-wide',
        });

        steps.push({
            intro:
                '<img class="sp-intro-animation" src="./assets/img/websites/intro/sp-intro-step-4-anim-new.gif">' +
                this.translate.instant('onboarding_description_step_4'),
            title: this.translate.instant('onboarding_title_step_4'),
            position: 'left',
            tooltipClass: 'sp-intro sp-intro-wide',
        });

        if (document.querySelector('#siteVersionDropdown')) {
            steps.push({
                element: document.querySelector('#siteVersionDropdown'),
                title: this.translate.instant('onboarding_title_step_7'),
                intro: this.translate.instant('onboarding_description_step_7'),
                position: 'left',
            });
        }

        if (document.querySelector('#publishButton')) {
            steps.push({
                element: document.querySelector('#publishButton'),
                title: this.translate.instant('onboarding_title_step_8'),
                intro: this.translate.instant('onboarding_description_step_8'),
                position: 'left',
            });
        }

        // steps.push({
        //     intro:
        //         '<img class="sp-intro-animation" src="' +
        //         this.getIntroAutoSaveVideoLink() +
        //         '">' +
        //         this.translate.instant('onboarding_description_step_6'),
        //     title: this.translate.instant('onboarding_title_step_6'),
        //     position: 'left',
        //     tooltipClass: 'sp-intro sp-intro-wide-lg sp-hide-skip',
        // });
        //
        // if (document.querySelector('#siteVersionDropdown')) {
        //     steps.push({
        //         element: document.querySelector('#siteVersionDropdown'),
        //         title: this.translate.instant('onboarding_title_step_7'),
        //         intro: this.translate.instant('onboarding_description_step_7'),
        //         position: 'left',
        //     });
        // }
        //
        // if (document.querySelector('#publishButton')) {
        //     steps.push({
        //         element: document.querySelector('#publishButton'),
        //         title: this.translate.instant('onboarding_title_step_8'),
        //         intro: this.translate.instant('onboarding_description_step_8'),
        //         position: 'left',
        //     });
        // }

        introJS.setOptions({
            ...this.introOptions,
            steps,
        });

        introJS.onbeforechange((targetElement) => {
            targetElement.scrollIntoView();
            return true;
        });

        introJS.oncomplete(() => {
            localStorage.setItem(this.ONBOARDING_KEY, JSON.stringify(true));
            this.store$.dispatch(AppActions.introFinished());
        });

        introJS.onexit(() => {
            localStorage.setItem(this.ONBOARDING_KEY, JSON.stringify(true));
            this.store$.dispatch(AppActions.introFinished());
        });

        introJS.start();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        localStorage.removeItem('constructor_open');
    }

    private subscribeToPageTitleUpdates(): void {
        combineLatest([this.pagesFacade.currentPage$, this.layoutFacade.isPreview$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([page, isPreview]) => {
                this.pageTitleService.updateTitle(page?.name, isPreview);
            });
    }
}
